import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import apiClient from '../utils/api';
import { useMode } from '../contexts/ModeContext';
import { ArrowLeft, Volume2, CheckCircle2 } from 'lucide-react';
import VoiceRecorder from '../components/VoiceRecorder';

// Build a streaming URL for the cached TTS endpoint.
// We intentionally do NOT pass a `voice` parameter — the backend picks the
// configured ElevenLabs voice (ELEVENLABS_VOICE_ID env). Passing OpenAI-style
// voice names like "shimmer" to ElevenLabs causes 404 voice_not_found.
const ttsUrl = (text) => {
  const base = ((typeof window !== 'undefined' && window.__hafizim_backend_url) || process.env.REACT_APP_BACKEND_URL || '');
  const params = new URLSearchParams({ text });
  return `${base}/api/elifba/tts?${params.toString()}`;
};

const LetterTile = ({ display, label, sublabel, thick, onPlay, kids, isRow, ttsText }) => {
  const [playing, setPlaying] = useState(false);
  const handle = async (e) => {
    e?.stopPropagation();
    setPlaying(true);
    try {
      await onPlay();
    } finally {
      setPlaying(false);
    }
  };
  const colorClass = thick
    ? kids
      ? 'bg-gradient-to-br from-rose-100 to-rose-200 border-rose-300'
      : 'bg-stone-100 border-stone-300'
    : kids
    ? 'bg-gradient-to-br from-sky-100 to-indigo-100 border-sky-200'
    : 'bg-white border-stone-200';
  return (
    <div
      className={`relative aspect-square p-3 flex flex-col items-center justify-center transition-all hover:scale-105 ${
        kids
          ? 'rounded-3xl shadow-[0_4px_0_0_rgba(0,0,0,0.1)] hover:shadow-[0_2px_0_0_rgba(0,0,0,0.1)] hover:translate-y-0.5'
          : 'rounded-xl hover:shadow-md'
      } border ${colorClass}`}
      data-testid={`tile-${label}`}
    >
      <button onClick={handle} className="flex-1 w-full flex flex-col items-center justify-center" data-testid={`tile-play-${label}`}>
        <div
          className={`font-arabic mb-1 leading-none ${isRow ? 'text-3xl md:text-4xl' : 'text-4xl md:text-5xl'}`}
          dir="rtl"
        >
          {display}
        </div>
        <div className="text-xs font-semibold text-stone-700">{label}</div>
        {sublabel && <div className="text-[10px] text-stone-500">{sublabel}</div>}
      </button>
      <Volume2
        className={`absolute top-1.5 left-1.5 w-3.5 h-3.5 ${
          playing ? 'text-emerald-600 animate-pulse' : 'text-stone-400'
        }`}
      />
      {ttsText && (
        <div className="absolute top-1 right-1">
          <VoiceRecorder expected={ttsText} apiClient={apiClient} testId={`voice-${label}`} size="sm" />
        </div>
      )}
    </div>
  );
};

const LetterFormsRow = ({ item, onPlay, kids }) => {
  // four forms displayed in RTL: isolated → initial → medial → final
  const items = [
    { display: item.forms.isolated, sub: 'tek' },
    { display: item.forms.initial, sub: 'başta' },
    { display: item.forms.medial, sub: 'ortada' },
    { display: item.forms.final, sub: 'sonda' },
  ];
  return (
    <div
      className={`p-4 ${
        kids
          ? 'bg-white rounded-2xl border-2 border-slate-100 shadow-[0_4px_0_0_rgba(0,0,0,0.08)]'
          : 'bg-white/80 rounded-xl border border-stone-200'
      }`}
      data-testid={`forms-row-${item.label}`}
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className={`font-bold ${kids ? 'font-kids' : 'font-elegant'} text-stone-800`}>
          {item.label}
          {item.thick && (
            <span className="ml-2 px-2 py-0.5 text-[10px] font-bold bg-rose-200 text-rose-800 rounded-full uppercase">
              Kalın
            </span>
          )}
        </h4>
        <button
          onClick={onPlay}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
          data-testid={`forms-play-${item.label}`}
        >
          <Volume2 className="w-3.5 h-3.5" />
          Dinle
        </button>
      </div>
      <div className="grid grid-cols-4 gap-2" dir="rtl">
        {items.map((i, idx) => (
          <div
            key={idx}
            className={`flex flex-col items-center justify-center p-3 ${
              kids ? 'bg-amber-50 rounded-xl' : 'bg-stone-50 rounded-lg'
            }`}
          >
            <div className="text-3xl font-arabic mb-1 leading-none" dir="rtl">
              {i.display}
            </div>
            <div className="text-[10px] text-stone-500">{i.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

function ModuleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isKidsMode } = useMode();
  const [mod, setMod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchModule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    // Stop audio on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const fetchModule = async () => {
    try {
      const [modRes, progRes] = await Promise.all([
        apiClient.get(`/elifba/modules/${slug}`),
        apiClient.get('/elifba/progress').catch(() => ({ data: null })),
      ]);
      setMod(modRes.data.module);
      const done = progRes?.data?.completed_modules?.includes(slug);
      setCompleted(!!done);
      setLoading(false);
    } catch (e) {
      toast.error('Modül yüklenirken hata oluştu');
      setLoading(false);
    }
  };

  const playText = (text) => async () => {
    if (!text) return;
    if (audioRef.current) {
      audioRef.current.pause();
    }
    try {
      const audio = new Audio(ttsUrl(text));
      audioRef.current = audio;
      await audio.play();
    } catch (e) {
      console.error('TTS play failed', e);
      toast('Ses oynatılamadı', { duration: 1500 });
    }
  };

  const markCompleted = async () => {
    try {
      setCompleting(true);
      await apiClient.post(`/elifba/complete-module/${slug}`);
      setCompleted(true);
      toast.success('Bölüm tamamlandı!', { duration: 1500 });
      // Wait for the toast to be visible then navigate. /elifba's own bootstrap()
      // will re-fetch progress; the small race is acceptable since the visit refreshes anyway.
      setTimeout(() => navigate('/elifba', { replace: true }), 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Hata oluştu');
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center" data-testid="module-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  if (!mod) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-6" data-testid="module-not-found">
        <div className="text-center">
          <div className="text-2xl text-stone-700 mb-4">Bölüm bulunamadı</div>
          <button
            onClick={() => navigate('/elifba')}
            className="px-6 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800"
          >
            Bölümlere Dön
          </button>
        </div>
      </div>
    );
  }

  const kids = isKidsMode;

  // Render based on module type
  const renderBody = () => {
    // Sectioned modules: thick_thin_read, med, shadda
    if (mod.type === 'thick_thin_read' || mod.type === 'med' || mod.type === 'shadda') {
      return (
        <div className="space-y-6" data-testid={`module-sections-${mod.type}`}>
          {mod.items.map((section) => {
            const isThickSection = section.section === 'thick' || section.section === 'fatha';
            return (
              <div
                key={section.key}
                className={`p-6 ${
                  kids
                    ? 'bg-white rounded-3xl border-2 border-slate-100 shadow-[0_6px_0_0_rgba(0,0,0,0.08)]'
                    : 'bg-white/80 backdrop-blur-sm rounded-xl border border-stone-200 shadow-sm'
                }`}
                data-testid={`section-${section.section}`}
              >
                <h3
                  className={`text-lg font-bold mb-4 ${
                    kids ? 'font-kids text-slate-800' : 'font-elegant text-stone-900'
                  }`}
                >
                  {section.section_label_tr}
                </h3>
                <div
                  className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 gap-3"
                  dir="rtl"
                >
                  {section.items.map((it) => (
                    <LetterTile
                      key={`${section.section}-${it.letter_id}`}
                      display={it.display}
                      label={it.label}
                      thick={it.thick !== undefined ? it.thick : isThickSection}
                      kids={kids}
                      onPlay={playText(it.tts_text)}
                      ttsText={it.tts_text}
                      isRow
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Single-grid modules: letters, hareke, cezm, tenwin
    if (
      mod.type === 'letters' ||
      mod.type === 'hareke' ||
      mod.type === 'cezm' ||
      mod.type === 'tenwin'
    ) {
      return (
        <div
          className={`p-6 ${
            kids
              ? 'bg-white rounded-3xl border-2 border-slate-100 shadow-[0_8px_0_0_rgba(0,0,0,0.08)]'
              : 'bg-white/80 backdrop-blur-sm rounded-xl border border-stone-200 shadow-sm'
          }`}
          data-testid="module-letters-grid"
        >
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 gap-3" dir="rtl">
            {mod.items.map((it) => (
              <LetterTile
                key={it.key}
                display={it.display}
                label={it.label}
                sublabel={it.letter_name || null}
                thick={it.thick}
                kids={kids}
                onPlay={playText(it.tts_text)}
                ttsText={it.tts_text}
              />
            ))}
          </div>
        </div>
      );
    }

    if (mod.type === 'letter_forms') {
      return (
        <div className="space-y-3" data-testid="module-forms-list">
          {mod.items.map((it) => (
            <LetterFormsRow key={it.key} item={it} kids={kids} onPlay={playText(it.tts_text)} />
          ))}
        </div>
      );
    }

    if (mod.type === 'lesson') {
      return (
        <div className="space-y-6" data-testid="module-lesson">
          {mod.intro_tr && (
            <div
              className={`p-5 ${
                kids
                  ? 'bg-gradient-to-r from-emerald-50 to-teal-50 rounded-3xl border-2 border-emerald-200'
                  : 'bg-emerald-50/60 rounded-xl border border-emerald-200'
              }`}
              data-testid="lesson-intro"
            >
              <p className="text-base text-emerald-900 leading-relaxed">{mod.intro_tr}</p>
            </div>
          )}
          {(mod.sections || []).map((section, sIdx) => (
            <div
              key={`section-${sIdx}`}
              className={`p-6 ${
                kids
                  ? 'bg-white rounded-3xl border-2 border-slate-100 shadow-[0_6px_0_0_rgba(0,0,0,0.08)]'
                  : 'bg-white/80 backdrop-blur-sm rounded-xl border border-stone-200 shadow-sm'
              }`}
              data-testid={`lesson-section-${sIdx}`}
            >
              <h3
                className={`text-lg font-bold mb-2 ${
                  kids ? 'font-kids text-slate-800' : 'font-elegant text-stone-900'
                }`}
              >
                {section.title_tr}
              </h3>
              <p className="text-sm text-stone-600 mb-4 leading-relaxed">{section.body_tr}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" dir="rtl">
                {(section.examples || []).map((ex, eIdx) => (
                  <button
                    key={`ex-${sIdx}-${eIdx}`}
                    onClick={playText(ex.tts_text || ex.display)}
                    className={`text-left p-4 transition-all hover:scale-[1.02] ${
                      kids
                        ? 'bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl border-2 border-amber-200 shadow-[0_4px_0_0_rgba(0,0,0,0.05)]'
                        : 'bg-amber-50/60 rounded-xl border border-amber-200 hover:shadow-md'
                    }`}
                    data-testid={`lesson-example-${sIdx}-${eIdx}`}
                  >
                    <div className="text-2xl md:text-3xl font-arabic mb-1 leading-tight" dir="rtl">
                      {ex.display}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs text-stone-700 flex-1" dir="ltr">{ex.label}</div>
                      <Volume2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="p-8 text-center text-stone-500" data-testid="module-empty">
        Bu bölüm yakında eklenecek.
      </div>
    );
  };

  const renderExercises = () => {
    if (!mod.exercises || mod.exercises.length === 0) return null;
    // Split into two pages of equal halves (mimic "2 sayfa alıştırma")
    const half = Math.ceil(mod.exercises.length / 2);
    const page1 = mod.exercises.slice(0, half);
    const page2 = mod.exercises.slice(half);

    const ExercisePage = ({ items, page }) => (
      <div
        className={`p-5 ${
          kids
            ? 'bg-amber-50/80 rounded-3xl border-2 border-amber-200'
            : 'bg-amber-50/60 rounded-xl border border-amber-200'
        }`}
        data-testid={`exercise-page-${page}`}
      >
        <div className="flex items-center justify-between mb-3">
          <h4 className={`font-bold ${kids ? 'font-kids text-amber-800' : 'font-elegant text-amber-900'}`}>
            📝 Alıştırma — Sayfa {page}
          </h4>
          <span className="text-xs text-amber-700">{items.length} adet</span>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" dir="rtl">
          {items.map((it, idx) => (
            <LetterTile
              key={`ex-${page}-${idx}`}
              display={it.display}
              label={it.label}
              thick={false}
              kids={kids}
              onPlay={playText(it.tts_text)}
              isRow
            />
          ))}
        </div>
      </div>
    );

    return (
      <div className="mt-10 space-y-4" data-testid="module-exercises">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-px flex-1 bg-amber-300"></div>
          <span className="text-sm font-semibold text-amber-700">UYGULAMA</span>
          <div className="h-px flex-1 bg-amber-300"></div>
        </div>
        <ExercisePage items={page1} page={1} />
        {page2.length > 0 && <ExercisePage items={page2} page={2} />}
      </div>
    );
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: kids
          ? 'linear-gradient(135deg, #FEF3C7 0%, #DBEAFE 50%, #E9D5FF 100%)'
          : 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)',
      }}
      data-testid="module-detail-page"
    >
      <header className="px-6 py-4 bg-white/60 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/elifba')}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
            data-testid="module-back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Bölümler</span>
          </button>
          <h1
            className={`text-xl md:text-2xl font-bold text-center ${
              kids ? 'font-kids text-green-600' : 'font-elegant text-emerald-900'
            }`}
            data-testid="module-title"
          >
            {mod.order}. {mod.title_tr}
          </h1>
          <div className="w-24"></div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <p className="text-sm text-stone-700 mb-6 text-center" data-testid="module-description">
          {mod.description_tr}
        </p>
        <p className="text-xs text-stone-500 mb-4 text-center">
          🔊 Bir harfe dokunarak sesini dinleyin
        </p>

        {renderBody()}
        {renderExercises()}

        <div className="mt-8 flex justify-center">
          <button
            onClick={markCompleted}
            disabled={completing || completed}
            className={`px-12 py-4 text-white text-lg font-bold transition-all transform hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-3 ${
              kids
                ? 'bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 rounded-2xl shadow-[0_6px_0_0_rgba(0,0,0,0.15)]'
                : 'bg-emerald-700 hover:bg-emerald-800 rounded-xl shadow-sm'
            }`}
            data-testid="complete-module-button"
          >
            <CheckCircle2 className="w-5 h-5" />
            {completed
              ? 'Tamamlandı ✓'
              : completing
              ? 'Kaydediliyor...'
              : 'Bölümü Tamamla'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ModuleDetail;
